/**
 * Pure helpers for the agent bar's model selector. The UI component
 * composes these into the Autocomplete options list; isolating the
 * logic here keeps it unit-testable without rendering.
 */

import type { OllamaInstalledModel } from '../../../services';

export type ProviderKey = 'ollama' | 'gemini';

export type ModelOptionSource = 'installed' | 'cloud-tag' | 'catalog';

export interface ModelOption {
  /** Model id sent to the BE (e.g. `"llama3.2"`, `"kimi-k2.6:cloud"`). */
  value: string;
  /** Display label in the dropdown. */
  label: string;
  /** Where the option came from — drives grouping in the UI. */
  source: ModelOptionSource;
  /**
   * Pre-formatted price shown alongside the label (e.g. `"$0.08/image"`).
   * Undefined when the model is unpriced — locally-run Ollama models have no
   * cost to quote, so nothing is rendered rather than a misleading "$0.00".
   */
  price?: string;
  /** Long-form price for the option's tooltip. */
  priceDetail?: string;
}

/**
 * Curated Ollama Cloud-tagged passthrough models. Always offered as
 * options because they don't show up in `/api/tags` — the daemon
 * resolves the `:cloud` suffix remotely without a local install.
 */
const OLLAMA_CLOUD_TAGS: ModelOption[] = [
  { value: 'kimi-k2.6:cloud', label: 'Kimi K2.6 (Cloud)', source: 'cloud-tag' },
  { value: 'glm-5.1:cloud', label: 'GLM 5.1 (Cloud)', source: 'cloud-tag' },
  { value: 'nemotron-3-super:cloud', label: 'Nemotron 3 Super (Cloud)', source: 'cloud-tag' },
  { value: 'gemma4:26b:cloud', label: 'Gemma 4 26B (Cloud)', source: 'cloud-tag' },
];

/**
 * Curated catalog of well-known local models — suggested even when not
 * installed yet, so users see a sensible "pull this" hint. Pruned to
 * the most-asked-for 2026 frontier set; the live `/api/tags` list takes
 * priority and de-dupes against this catalog.
 */
const OLLAMA_LOCAL_SUGGESTIONS: ModelOption[] = [
  { value: 'llama3.2', label: 'Llama 3.2', source: 'catalog' },
  { value: 'gemma4', label: 'Gemma 4', source: 'catalog' },
  { value: 'qwen3.6', label: 'Qwen 3.6', source: 'catalog' },
  { value: 'qwen3-coder-next', label: 'Qwen3 Coder Next', source: 'catalog' },
  { value: 'glm-4.7-flash', label: 'GLM 4.7 Flash', source: 'catalog' },
];

/** The shape of a catalog entry this module needs: a provider and its model ids. */
export interface CatalogEntryLike {
  key: string;
  models: { id: string }[];
}

/**
 * Whether the catalog still offers `model` for `provider`.
 *
 * A stored selection outlives the catalog: Google retired the Gemini 2.0 family, and a
 * browser that picked one kept sending it with every operation, which the provider
 * answered with a 404. False here tells the caller to reseed from the catalog.
 *
 * A provider absent from this catalog view (Ollama when the daemon is down, say) returns
 * true — we can't tell "retired" from "not listed right now", and clearing a working
 * selection because the daemon blipped would be worse.
 */
export function modelStillOffered(
  entries: CatalogEntryLike[],
  provider: string,
  model: string,
): boolean {
  const entry = entries.find(e => e.key === provider);
  if (!entry) return true;
  return entry.models.some(m => m.id === model);
}

/**
 * Build the Ollama model dropdown options. Priority:
 *   1. Models actually installed (from `/api/tags`).
 *   2. Curated cloud-tagged passthrough models.
 *   3. Suggested catalog (local pull suggestions) that aren't already installed.
 *
 * Stable order within each group; de-duplicated by `value`. The model
 * id is preserved verbatim from `/api/tags` — Ollama uses `name:tag`
 * conventions (e.g. `"llama3.2:latest"`); the BE accepts these as-is.
 */
export function ollamaModelOptions(installed: OllamaInstalledModel[]): ModelOption[] {
  const seen = new Set<string>();
  const out: ModelOption[] = [];

  for (const m of installed) {
    const value = m.name.trim();
    if (!value || seen.has(value)) continue;
    seen.add(value);
    out.push({ value, label: m.name, source: 'installed' });
  }

  for (const opt of OLLAMA_CLOUD_TAGS) {
    if (seen.has(opt.value)) continue;
    seen.add(opt.value);
    out.push(opt);
  }

  for (const opt of OLLAMA_LOCAL_SUGGESTIONS) {
    if (seen.has(opt.value)) continue;
    seen.add(opt.value);
    out.push(opt);
  }

  return out;
}
